import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { PostAttachmentsApiCreateInterface } from 'modules/community/types/post-attachment'
import type { PostInterface } from 'modules/community/types/post-interface'
import { communityPostKeys } from './key-factory'

export interface CreatePostBody {
  title: string
  path: string
  content: string
  topic?: number
  scheduledTo?: string | null
  attachments?: PostAttachmentsApiCreateInterface[]
}

export const useCreatePost = ({
  communityPath,
  onSuccess,
  onError,
}: {
  communityPath: string
  onSuccess?: (data: PostInterface) => void
  onError?: (error: Error) => void
}): UseMutationResult<PostInterface, Error, CreatePostBody> => {
  const queryClient = useQueryClient()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    PostInterface,
    CreatePostBody
  >({
    method: RequestMethodsEnum.post,
  })

  return useMutation<PostInterface, Error, CreatePostBody>({
    mutationFn: body => fetcher(`${COMMUNITY_API_PREFIX}/${communityPath}/post`, body),
    retry: false,
    onSuccess: onSuccess ? data => onSuccess(data) : undefined,
    onError: onError ? error => onError(error) : undefined,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
    },
  })
}
